import {inject, Injectable} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  timeout,
} from 'rxjs';

import {
  CittadinoServiziService,
  CittadinoServizioDTO,
} from '../../core/api/generated';
import {
  getServizioLogicoByTipoBE,
  ServizioLogico,
} from '../../shared/constants/servizio-logico.const';
import {ServizioAttivo} from '../../shared/interfaces/servizio-attivo.interface';
import {
  statoServizio,
  StatoServizio,
} from '../../features/cittadino/mensa/studenti/home/anno-scolastico.util';
import {LocalStorageService} from '../local-storage.service';
import {GlobalManagerComuneService} from './global-manager-comune.service';

/**
 * Frontiera UNICA verso l'asse ACCESSI: i servizi attivi sbloccati dal cittadino
 * (`GET /api/v1/cittadino/servizi`). È l'unico consumatore di `CittadinoServizioDTO`:
 * normalizza i campi opzionali in `ServizioAttivo` (campi garantiti), filtra sul comune
 * attivo (contesto sticky), classifica per tipo ed espone proiezioni coerenti.
 *
 * Una GET per sessione (`shareReplay(1)`), invalidabile con `refresh()` dopo eventi che
 * cambiano i servizi sbloccati (es. accettazione di una pratica). Il filtro comune NON
 * rifà la chiamata: è applicato in proiezione su `comuneSelezionato$`, così il cambio
 * comune ri-filtra a costo zero. Vedi change servizi-attivi-cittadino (design §D1–D5).
 *
 * Distinto da `CatalogoServiziCittadinoService` (asse catalogo: "cosa posso richiedere",
 * derivato dai bandi aperti). Qui: "cosa ho sbloccato e posso usare".
 */
@Injectable({providedIn: 'root'})
export class ServiziAttiviService {
  private readonly api = inject(CittadinoServiziService);
  private readonly managerComune = inject(GlobalManagerComuneService);
  private readonly localStorage = inject(LocalStorageService);

  /** Trigger di refresh: ogni emissione forza una nuova GET (la prima parte subito). */
  private readonly refresh$ = new BehaviorSubject<void>(undefined);

  /**
   * Stato di errore dell'ultima GET servizi, DISTINTO dall'empty-state. Un fallimento di rete
   * fa emettere `serviziAttiviTutti$` una lista vuota (per non avvelenare lo `shareReplay`) e
   * setta questo flag a `true`; una GET riuscita lo riporta a `false`. La pagina "I miei servizi"
   * lo consuma per mostrare l'errore azionabile invece di un falso "nessun servizio". Vedi change
   * fix-mensa-context-race-rxjs-cleanup (B2).
   */
  private readonly serviziError$ = new BehaviorSubject<boolean>(false);

  /** Stato di errore osservabile in sola lettura (vedi `serviziError$`). */
  readonly serviziInErrore$: Observable<boolean> = this.serviziError$.asObservable();

  /**
   * Scelta d'istanza mensa fatta dall'utente (asse ACCESSI). La sede dello stato è QUI,
   * accanto alle proiezioni che la consumano (`servizioAttivoDiTipo$`), perché questo
   * servizio è la frontiera unica dell'asse accessi (open question del design risolta:
   * non un holder gemello di GlobalManagerServizioService, che vive sull'asse logico).
   * Seed dallo storage all'avvio; `null` = nessuna scelta → fallback auto "più viva".
   * La scelta "resta finché valida" (id ancora tra le istanze attive del comune): la
   * validazione è in `servizioAttivoDiTipo$`, qui si tiene solo l'id grezzo persistito.
   */
  private readonly istanzaMensaSelezionataId$ = new BehaviorSubject<string | null>(null);

  constructor() {
    // Seed iniziale dalla persistenza (suggerimento, non autorevole: validato a valle).
    this.localStorage
      .getIstanzaMensaSelezionata$()
      .subscribe((id) => this.istanzaMensaSelezionataId$.next(id));
  }

  /**
   * Servizi della sessione corrente; `null` = nessuna sessione. La GET parte solo da loggati e
   * riparte a ogni login: senza sessione (visitatore anonimo del bando pubblico) una GET
   * prenderebbe 401, e l'interceptor la chiude con `EMPTY` — nessun valore, nemmeno il `[]` del
   * `catchError` — lasciando lo stream condiviso senza valore fino al ricaricamento della pagina:
   * dopo il login la Home restava sullo spinner. Il `null` al logout scalza anche la lista
   * dell'utente precedente dal replay. I flussi pubblici filtrano il `null` DOPO il proprio
   * `shareReplay`: chi si sottoscrive senza sessione aspetta il login invece di leggere dati vecchi.
   */
  private readonly serviziDiSessione$: Observable<ServizioAttivo[] | null> = combineLatest([
    this.localStorage.checkLoggedUser$.pipe(distinctUntilChanged()),
    this.refresh$,
  ]).pipe(
    switchMap(([loggato]) => (loggato ? this.caricaServizi$() : of(null))),
    shareReplay({bufferSize: 1, refCount: false})
  );

  /**
   * Tutte le istanze normalizzate CROSS-COMUNE, una GET per ciclo di refresh, condivisa.
   * Esposta per la pagina "I miei servizi" (hub di tutti gli accessi, raggruppati per
   * comune); i gate scopati sul comune attivo usano `serviziAttivi$` (filtrato). Il filtro
   * comune è applicato a valle, NON rifà la chiamata.
   */
  readonly serviziAttiviTutti$: Observable<ServizioAttivo[]> =
    this.serviziDiSessione$.pipe(filter(isLista));

  /** Lista granulare dei servizi attivi del comune corrente. Proiezione, no GET extra. */
  readonly serviziAttivi$: Observable<ServizioAttivo[]> = combineLatest([
    this.serviziDiSessione$,
    this.managerComune.comuneSelezionato$,
  ]).pipe(
    map(([servizi, comune]) => {
      if (servizi === null) {
        return null;
      }
      const comuneId = comune?.id ?? null;
      return comuneId ? servizi.filter((s) => s.comuneId === comuneId) : [];
    }),
    shareReplay({bufferSize: 1, refCount: false}),
    filter(isLista)
  );

  /** Categorie (ServizioLogico) distinte presenti tra i servizi attivi: alimenta lo switcher. */
  readonly tipiAttivi$: Observable<ServizioLogico[]> = this.serviziAttivi$.pipe(
    map((servizi) => {
      const visti = new Map<string, ServizioLogico>();
      for (const s of servizi) {
        if (!visti.has(s.tipo.slug)) {
          visti.set(s.tipo.slug, s.tipo);
        }
      }
      return [...visti.values()];
    })
  );

  /** `true` se esiste almeno un servizio attivo del tipo dato (gate per home/menu). */
  èTipoAttivo$(slug: string): Observable<boolean> {
    return this.serviziAttivi$.pipe(map((servizi) => servizi.some((s) => s.tipo.slug === slug)));
  }

  /**
   * Istanza attiva selezionata per un tipo. Ordine di scelta (change
   * select-servizio-post-accettazione, design §D3/D4):
   *  1. la scelta d'istanza persistita, SE valida — l'id è tra le istanze attive del
   *     tipo nel comune corrente ("resta finché valida"); la scelta vale per la sola
   *     mensa (`slug === 'mensa'`), unico tipo con selezione d'istanza.
   *  2. fallback auto-selezione "più viva": prima senza scadenza (`dataFine` null),
   *     poi `dataInizio` DESC.
   * `null` se il tipo non ha istanze attive. Le altre istanze restano in `serviziAttivi$`.
   */
  servizioAttivoDiTipo$(slug: string): Observable<ServizioAttivo | null> {
    return combineLatest([this.serviziAttivi$, this.istanzaMensaSelezionataId$]).pipe(
      map(([servizi, sceltaId]) => {
        const delTipo = servizi.filter((s) => s.tipo.slug === slug);
        if (delTipo.length === 0) {
          return null;
        }
        // Scelta persistita valida (solo mensa): id ancora tra le istanze attive del tipo.
        if (slug === 'mensa' && sceltaId) {
          const scelta = delTipo.find((s) => s.id === sceltaId);
          if (scelta) {
            return scelta;
          }
          // Scelta stale (id sparito dalla lista attiva) → ricade sul fallback "più viva".
        }
        return [...delTipo].sort(confrontaPerVivacita)[0];
      })
    );
  }

  /**
   * Imposta la scelta d'istanza mensa dell'utente e la persiste (asse ACCESSI). Sorgente
   * UNICA della scrittura: i componenti non scrivono direttamente in LocalStorage.
   */
  /**
   * La pagina «Tariffe» è visibile per l'istanza mensa corrente: è il Comune a deciderlo dalla
   * dashboard (flag del servizio, nasce spento). Governa voce di menu, box «Importo pasto» e
   * accesso diretto alla rotta; il backend risponde comunque 404 se il flag è spento.
   */
  readonly tariffeMensaVisibili$: Observable<boolean> = this.servizioAttivoDiTipo$('mensa').pipe(
    map((servizio) => servizio?.tariffeVisibili === true),
    distinctUntilChanged()
  );

  setIstanzaMensaSelezionata(id: string): void {
    this.istanzaMensaSelezionataId$.next(id);
    this.localStorage.setIstanzaMensaSelezionata$(id).subscribe();
  }

  /** L'id della scelta d'istanza mensa corrente (grezzo, non validato). `null` se nessuna. */
  istanzaMensaSelezionata(): string | null {
    return this.istanzaMensaSelezionataId$.getValue();
  }

  /** Azzera la scelta d'istanza (stato + persistenza). Usato dal cambio comune. */
  resetIstanzaMensaSelezionata(): void {
    this.istanzaMensaSelezionataId$.next(null);
    this.localStorage.removeIstanzaMensaSelezionata$().subscribe();
  }

  /**
   * Id dell'istanza mensa attiva (auto-selezionata), o `null` se nessuna. È il valore dell'header
   * `Gestione-Mense-Servizio-Id` richiesto dagli endpoint mensa per-servizio del cittadino
   * (menu, discovery studenti, …). Sorgente UNICA per l'header. Vedi [[mensa-discovery-due-livelli]].
   */
  idServizioMensaAttivo$(): Observable<string | null> {
    return this.servizioAttivoDiTipo$('mensa').pipe(map((s) => s?.id ?? null));
  }

  /**
   * Variante Promise di [[idServizioMensaAttivo$]] per i flussi imperativi (es. command palette).
   * Un fallimento della GET servizi degrada a `null` (`catchError`): i chiamanti gestiscono già
   * "nessun servizio mensa" — così un errore di rete non lascia spinner appesi né rejection non
   * gestite. Sorgente UNICA del wrapper Promise: i componenti NON ridefiniscono `firstValueFrom`.
   */
  idServizioMensaAttivo(): Promise<string | null> {
    return firstValueFrom(this.idServizioMensaAttivo$().pipe(catchError(() => of(null))));
  }

  /**
   * Istanza CORRENTE per un tipo = quella il cui intervallo CONTIENE oggi
   * (`dataInizio ≤ oggi ≤ dataFine`), usata per l'etichetta annualità della home-mensa.
   *
   * `dataFine` può MANCARE (`null` = senza scadenza): in quel caso il limite superiore è
   * aperto, quindi "contiene oggi" sse `dataInizio ≤ oggi`. Se nessuna istanza contiene oggi
   * (es. anno non ancora iniziato, o solo annualità passate/future), ricade sull'istanza "più
   * viva" (`servizioAttivoDiTipo$`, stessa scelta dell'header) per restare coerente col contesto.
   * `null` se il tipo non ha istanze attive.
   */
  servizioCorrenteDiTipo$(slug: string): Observable<ServizioAttivo | null> {
    return this.serviziAttivi$.pipe(
      map((servizi) => {
        const delTipo = servizi.filter((s) => s.tipo.slug === slug);
        if (delTipo.length === 0) {
          return null;
        }
        const oggi = oggiIso();
        const contieneOggi = delTipo.filter((s) => {
          const iniziato = !!s.dataInizio && s.dataInizio <= oggi;
          // dataFine null = senza scadenza → limite superiore aperto.
          const nonScaduto = s.dataFine === null || s.dataFine >= oggi;
          return iniziato && nonScaduto;
        });
        const candidate = contieneOggi.length > 0 ? contieneOggi : delTipo;
        return [...candidate].sort(confrontaPerVivacita)[0];
      })
    );
  }

  /** Istanza mensa CORRENTE (contiene oggi, fallback più viva): sorgente dell'annualità. */
  servizioMensaCorrente$(): Observable<ServizioAttivo | null> {
    return this.servizioCorrenteDiTipo$('mensa');
  }

  /**
   * Stato del ciclo di vita del servizio mensa come SEGNALE DI CONTESTO PURO
   * (`imminente`/`attivo`/`terminato`), derivato dalle date dell'istanza mensa CORRENTE —
   * la stessa sorgente dell'etichetta annualità (`servizioMensaCorrente$()`), così stato ed
   * etichetta restano coerenti per costruzione. `null` se non c'è istanza mensa.
   *
   * NON governa le azioni: il gating della ricarica resta al flag di config
   * `generazione_ricariche_abilitata` + codici errore di dominio del BE. Vedi change
   * `cittadino-mensa-ciclo-vita-servizio`.
   */
  statoServizioMensa$(): Observable<StatoServizio | null> {
    return this.servizioMensaCorrente$().pipe(
      map((istanza) =>
        istanza ? statoServizio(istanza.dataInizio, istanza.dataFine, oggiIso()) : null
      )
    );
  }

  /** Invalida la cache e ricarica (es. dopo l'accettazione di una pratica). */
  refresh(): void {
    this.refresh$.next();
  }

  /**
   * Una GET dei servizi, normalizzata. `catchError` DENTRO l'inner observable: un fallimento
   * degrada a `[]` senza far terminare con errore lo `shareReplay` (resterebbe avvelenato,
   * rendendo `refresh()` un no-op), e accende l'error-state distinto dall'empty.
   */
  private caricaServizi$(): Observable<ServizioAttivo[]> {
    this.serviziError$.next(false);
    return this.api.listServiziCittadino().pipe(
      // Tetto alla risposta: Home, menu e selezione del servizio aspettano questa lista, e una
      // GET che non torna mai li lascerebbe sullo spinner. Oltre il tetto vale come errore.
      timeout(SERVIZI_TIMEOUT_MS),
      catchError(() => {
        this.serviziError$.next(true);
        return of([] as CittadinoServizioDTO[]);
      }),
      map((dtos) => (dtos ?? []).map((dto) => this.normalizza(dto)).filter(isServizioAttivo))
    );
  }

  /**
   * Normalizza un DTO grezzo in `ServizioAttivo` o `null` se degenere:
   * manca `id`, oppure `tipo_servizio` non è classificabile in un ServizioLogico.
   * `data_fine` assente → `null` (servizio senza scadenza).
   */
  private normalizza(dto: CittadinoServizioDTO): ServizioAttivo | null {
    const tipo = getServizioLogicoByTipoBE(dto.tipo_servizio);
    if (!dto.id || !tipo) {
      return null;
    }
    return {
      id: dto.id,
      tipo,
      comuneId: dto.comune_id ?? '',
      comuneNome: dto.comune_nome ?? '',
      comuneSlug: dto.comune_slug ?? '',
      codiceBando: dto.tipo_pratica_codice ?? '',
      dataInizio: dto.data_inizio ?? '',
      dataFine: dto.data_fine ?? null,
      tariffeVisibili: dto.tariffe_visibili === true,
    };
  }
}

/** Oltre questo tempo la GET dei servizi conta come fallita (stato di errore, non spinner). */
const SERVIZI_TIMEOUT_MS = 20_000;

/** Type-guard che scarta il `null` di "nessuna sessione" dai flussi dei servizi. */
function isLista(servizi: ServizioAttivo[] | null): servizi is ServizioAttivo[] {
  return servizi !== null;
}

/** Type-guard per stringere `(ServizioAttivo | null)[]` dopo il map di normalizzazione. */
function isServizioAttivo(s: ServizioAttivo | null): s is ServizioAttivo {
  return s !== null;
}

/**
 * Data odierna in formato `YYYY-MM-DD` (locale), comparabile lessicograficamente con i campi
 * `dataInizio`/`dataFine` dei servizi (stesso formato BE). Locale, non UTC: l'anno scolastico
 * è un concetto di calendario locale.
 */
function oggiIso(): string {
  const d = new Date();
  const mese = `${d.getMonth() + 1}`.padStart(2, '0');
  const giorno = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${mese}-${giorno}`;
}

/**
 * Ordina per "vivacità" (auto-selezione): senza scadenza prima, poi data_inizio DESC.
 * Estende l'ordinamento BE (`data_inizio DESC`) con la priorità "senza scadenza".
 */
function confrontaPerVivacita(a: ServizioAttivo, b: ServizioAttivo): number {
  const aSenzaScadenza = a.dataFine === null;
  const bSenzaScadenza = b.dataFine === null;
  if (aSenzaScadenza !== bSenzaScadenza) {
    return aSenzaScadenza ? -1 : 1;
  }
  // Entrambe con (o senza) scadenza: la più recente per data_inizio prima.
  return b.dataInizio.localeCompare(a.dataInizio);
}
